import { Box, Button, Card, CardContent, FormHelperText, Grid, CardHeader,
  Drawer, useMediaQuery, MenuItem, Autocomplete, TextField as MuiTextField } from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { addMinutes } from 'date-fns'
import { useFormik } from "formik";
import { CRUD_GET_LIST, useGetMainList } from "ra-core";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "@/components/formik/OdForm";
import SelectField from "@/components/formik/SelectField";
import TextField from "@/components/formik/TextField";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { DatabaseResponseDto } from "@/gen/api/backup";
import { DataAccessTokenDto } from "@/gen/api/encrypt";
import { DataAccessRoleResponseDto } from "@/gen/api/governance";
import { UserOptionResponseDto, UserStatus } from "@/gen/api/user";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import { useUserTimeFormat } from "@/utils/userTimeFormat";
import { DataAccessTokenDetailInternal } from "./DataAccessTokenDetail";
import { useSelector } from "react-redux";
import { getUserProfile } from "@/reducers/userProfile";


const validationSchema =
  Yup.object().shape({
    databaseId: Yup.number().required('Database is required'),
    tokenName: Yup.string().max(50).required('Token Name is required'),
    consumerUserId: Yup.number().required('Consumer User is required'),
    expirationTime: Yup.string().required('Expiration Time is required'),
    dataAccessRoles: Yup.array().min(1, 'Data Access Roles is required'),
  })
  
  
export interface CreateDataAccessTokenProps {
  open: boolean;
  setOpen: (x:boolean) => void;
}

const getInitialValues = () => ({
  now: new Date(),
  submit: '',
  databaseId: 0,
  tokenName: '',
  consumerUserId: '',
  expirationTime: addMinutes(new Date(), 30),
  dataAccessRoles: '',
})

const CreateDataAccessToken: FC<CreateDataAccessTokenProps> = ({
  open,
  setOpen,
}) => {
  const format = useUserTimeFormat()
  const userProfile = useSelector(getUserProfile)
  const save = useSimpleSaveAction()
  const [states, setStates] = useState({ 
    copied: false,
    tokenDto: null as DataAccessTokenDto|null,
    expirationReached: false,
    invalidated: false,
  })

  const onSubmit: OnSubmit<ReturnType<typeof getInitialValues>> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    const options = {
      onSuccess: ({data}: {data:DataAccessTokenDto}) => {
        setStatus({ success: true })
        setSubmitting(false)
        
        const expirationReached = new Date(data.expirationTime || 0).toISOString() < new Date().toISOString()
        setStates(v => ({
          ...v, 
          tokenDto: data,
          expirationReached,
          invalidated: data.tokenInvalidated || data.keyInvalidated || expirationReached,
        }))
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }

    const payload = {
      ...values,
      expirationTime: format(values.expirationTime, "yyyy-MM-dd'T'HH:mm")
    }

    save(payload, options)
  }, [save, format])

  const initialValues = useMemo(() => getInitialValues(), [])
  const validate = (values: typeof initialValues) => {
    if (!values.dataAccessRoles || values.dataAccessRoles.length < 1) {
      return { 'dataAccessRoles': 'Data Access Roles is required' }
    }
  }

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema, validate })
  const { errors, touched, isSubmitting, values, status, setFieldValue, resetForm } = formikCtrl

  useEffect(() => {
    if (open) {
      setStates(v => ({...v, tokenDto: null, expirationReached: false, invalidated: false}))
      resetForm({
        values: getInitialValues(),
      })
    }
  }, [open, resetForm])

  const handleClose = (): void => {
    setOpen(false);
  };

  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480

  const { databases, users, dataAccessRoles } = useDataSources() // ??

  const getTzDate = (d: Date|null|undefined) => {
    return d && userProfile?.ianaTimeZone ? utcToZonedTime(d, userProfile?.ianaTimeZone) : d
  }
  const fromTzDate = (d: Date|null) => {
    return d && userProfile?.ianaTimeZone ? zonedTimeToUtc(d, userProfile?.ianaTimeZone) : d
  }
  return (
    <Drawer
      anchor="right"
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          p: 2,
          width: width,
        }
      }}
    >
      <OdForm formikCtrl={formikCtrl}>
          
        <Card sx={{ overflowY: 'auto', mb: 4, }} >
          <CardHeader title="Create Data Access Token" />
          <CardContent>
            <Grid container spacing={4} >
              {states.tokenDto
                ? <DataAccessTokenDetailInternal tokenDto={states.tokenDto} expirationReached={states.expirationReached} invalidated={states.invalidated} />
                : <>
                    <Grid item xs={12} >
                      <SelectField label="Target Database*" name="databaseId" >
                        <MenuItem value={0}>All Databases</MenuItem>
                        {databases.map(d => (
                          <MenuItem key={d.databaseId} value={d.databaseId || 0}>{d.name}</MenuItem>                    
                        ))}
                      </SelectField>
                    </Grid>
                    <Grid item xs={12} >
                      <TextField label="Token Name*" name="tokenName" />
                    </Grid>
                    <Grid item xs={12} >
                      <SelectField label="Consumer User*" name="consumerUserId" >
                        {users.map(u => (
                          <MenuItem key={u.userId} value={u.userId}>{u.firstName} {u.lastName} ({u.username})</MenuItem>
                        ))}
                      </SelectField>
                    </Grid>
                    <Grid item xs={12} >
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                      >
                        <DateTimePicker 
                          label="Expiration Time*"
                          // renderInput={(params) => (
                          //   <MuiTextField 
                          //     {...params}
                          //     error={Boolean(touched['expirationTime'] && errors['expirationTime'])} 
                          //     fullWidth
                          //     helperText={touched['expirationTime'] && errors['expirationTime']}
                          //     variant="outlined"
                          //   />
                          // )}
                          ampm={false}
                          minDateTime={getTzDate(values['now'])}
                          value={getTzDate(values['expirationTime'])}
                          onChange={(newValue) => setFieldValue('expirationTime', fromTzDate(newValue))}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} >
                      <Autocomplete 
                        options={dataAccessRoles}
                        getOptionLabel={(option) => option ? (option.name || '') : ''}
                        renderInput={(params) => (
                          <MuiTextField 
                            {...params}
                            label="Data Access Roles*"
                            error={Boolean(touched['dataAccessRoles'] && errors['dataAccessRoles'])} 
                            fullWidth
                            helperText={touched['dataAccessRoles'] && errors['dataAccessRoles']}
                            variant="outlined"
                          />
                        )}
                        multiple
                        onChange={(event, value, reason) => {
                          if ((reason === 'selectOption' || reason === 'removeOption') && value) {
                            const selectedRoles = value.map(v => v.value)
                            setFieldValue('dataAccessRoles', selectedRoles)
                          }
                        }}
                      />
                    </Grid>
                  </>
              }
            </Grid>
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {errors.submit}
                </FormHelperText>
              </Box>
            )}
          </CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: 2
            }}
          >
            <Button
              color="primary"
              disabled={isSubmitting}
              variant="outlined"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={isSubmitting || status?.success}
              type="submit"
              variant="contained"
            >
              Create
            </Button>
          </Box>
        </Card>
      </OdForm>
    </Drawer>
  )
}

export default CreateDataAccessToken;


const useDataSources = () => {
  const dbData = useGetMainList('databases', pages(), sorts(), {}, { action: CRUD_GET_LIST })
  const userData = useGetMainList('users-browse', {page:1, perPage: 2000}, sorts(), {}, { action: CRUD_GET_LIST })
  const darData = useGetMainList('settings/governance/data-access-role', pages(), sorts(), {}, { action: CRUD_GET_LIST });

  return useMemo(() => {
    const databases = (dbData.ids || []).map(id => (dbData.data || {})[id] as DatabaseResponseDto).sort((a, b) => (a.name || '') < (b.name || '') ? -1 : 1)
    const users = 
      (userData.ids || []).map(id => (userData.data || {})[id] as UserOptionResponseDto)
        .filter(u => u.status === UserStatus.Active)
        .sort((a, b) => (`${a.firstName} ${a.lastName}`) < (`${b.firstName} ${b.lastName}`) ? -1 : 1)
    const dataAccessRoles = (darData.ids || []).map(id => (darData.data || {})[id] as DataAccessRoleResponseDto).sort((a, b) => (a.name || '') < (b.name || '') ? -1 : 1)
    
    return { databases, users, dataAccessRoles }
  }, [dbData, userData, darData])
}
